/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import {
  EuiBadge,
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiTextColor,
  useEuiTheme,
} from '@elastic/eui'

import type { AdminConsoleCCMCluster } from '@modules/ccm-admin-lib/types'
import { isCreatedByInternalUser, isDisconnectedCluster } from '@modules/ccm-admin-lib/util'
import { FormattedMessage } from '@modules/i18n'

import type { FC } from 'react'

const ClusterIdTableCell: FC<{ cluster: AdminConsoleCCMCluster }> = ({ cluster }) => {
  const { euiTheme } = useEuiTheme()

  const textStyleSemiBold = { fontWeight: euiTheme.font.weight.semiBold }

  const idElement = isDisconnectedCluster(cluster) ? (
    <EuiTextColor color='subdued'>{cluster.self_managed_cluster.id}</EuiTextColor>
  ) : (
    <EuiText css={textStyleSemiBold}>{cluster.self_managed_cluster.id}</EuiText>
  )

  return (
    <EuiFlexGroup direction='column' gutterSize='s'>
      <EuiFlexItem>{idElement}</EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiFlexGroup direction='row' gutterSize='s' alignItems='center'>
          {isDisconnectedCluster(cluster) && (
            <EuiFlexItem grow={false}>
              <EuiBadge color='default'>
                <FormattedMessage
                  id='ccm.cluster-table.disconnected-badge'
                  defaultMessage='Disconnected'
                />
              </EuiBadge>
            </EuiFlexItem>
          )}
          {isCreatedByInternalUser(cluster) && (
            <EuiFlexItem grow={false}>
              <EuiBadge color='default'>
                <FormattedMessage
                  id='ccm.cluster-table.internal-user-badge'
                  defaultMessage='Internal'
                />
              </EuiBadge>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

export default ClusterIdTableCell
